/**
 * Canonical accessibility props for a play/pause control (TES-1581).
 *
 * ## Why this exists
 *
 * Before this module, every play control in the app conveyed playback state
 * *only* by flipping its own accessible name (`"Play X"` ↔ `"Pause X"`), with
 * no `aria-pressed`. Re-announcement of a changed name on the focused element
 * is inconsistent across screen readers, so in practice the state change was
 * often silent. See finding K3 in
 * `docs/design-system/a11y-baseline-2026-08-08.md`.
 *
 * ## The rule this encodes
 *
 * A play control is a **toggle button**, and the WAI-ARIA Authoring Practices
 * are explicit that a toggle button must keep a *stable* label — the state
 * lives in `aria-pressed`, not in the name. Doing both produces the
 * contradictory announcement *"Pause Vine Boom, pressed"* and double-signals
 * the same transition.
 *
 * So:
 *
 * - the accessible name is always the action + subject (`"Play Vine Boom"`),
 *   never `"Pause …"`;
 * - `aria-pressed` is the single channel for play/pause state;
 * - `aria-busy` carries the transient loading state, so buffering never
 *   churns the name either.
 *
 * `aria-pressed` is deliberately **omitted** whenever the control is not
 * presenting as a play/pause toggle (an error, or a caller-supplied `label`
 * for a temporary alternate action) — a toggle state on a control that cannot
 * currently toggle is worse than none.
 *
 * There is intentionally **no live region** paired with this. TES-1579 keeps
 * focus on the control through activation, so the `aria-pressed` change is
 * announced on the focused element; a live region on top would announce the
 * same transition twice.
 */

export interface PlayToggleA11yOptions {
  /** True while this control's track is playing. */
  playing: boolean;
  /**
   * Subject of the control — normally the track title. Omit for bare controls
   * whose subject is already clear from context (e.g. the create-tool preview,
   * which sits next to the file it plays).
   */
  title?: string | null;
  /** True while the track is resolving or buffering. */
  loading?: boolean;
  /** Set when playback failed. Replaces the name and drops the toggle state. */
  error?: string | null;
  /**
   * Full override of the accessible name, for a mode in which the control
   * temporarily does something other than play/pause. Also drops
   * `aria-pressed`, because in that mode it is not a play/pause toggle.
   */
  label?: string | null;
  /** Action verb for the stable name. Defaults to `"Play"`. */
  verb?: string;
}

export interface PlayToggleA11yProps {
  "aria-label": string;
  /** Omitted (not `false`) when the control is not acting as a toggle. */
  "aria-pressed"?: boolean;
  /** Omitted (not `false`) when not loading, to keep the DOM quiet. */
  "aria-busy"?: boolean;
}

/**
 * Build the `aria-*` props for a play/pause toggle. Spread onto the `<button>`:
 *
 * ```tsx
 * <button type="button" {...playToggleA11y({ playing, title, loading })} />
 * ```
 */
export function playToggleA11y({
  playing,
  title,
  loading = false,
  error = null,
  label = null,
  verb = "Play",
}: PlayToggleA11yOptions): PlayToggleA11yProps {
  const subject = title?.trim();
  const stableName = subject ? `${verb} ${subject}` : verb;

  // An override or an error means the control is not a play/pause toggle right
  // now, so it gets a bespoke name and no `aria-pressed`.
  const overrideName = label?.trim() || (error ? `Playback error: ${error}` : null);

  const props: PlayToggleA11yProps = {
    "aria-label": overrideName ?? stableName,
  };

  if (!overrideName) props["aria-pressed"] = playing;
  if (loading) props["aria-busy"] = true;

  return props;
}
